import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidPercentageDecimal,
  isWithinBounds,
  useGetFootprintingDairyHerdDetails,
  useMutateFootprintingDairyHerdDetails,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, PageSpin, Typography } from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const HerdOffspringForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [matFeedPct, setMatFeedPct] = useState<number | null>(null);
  const [wholeMilkFeedPct, setWholeMilkFeedPct] = useState<number | null>(null);
  const [ageOfFirstCalvingMonths, setAgeOfFirstCalvingMonths] = useState<number | null>(null);
  const [timeBetweenCalvingDays, setTimeBetweenCalvingDays] = useState<number | null>(null);
  const [hasTimeBetweenError, setHasTimeBetweenError] = useState(false);

  const { data: dairyHerdDetails, isSuccess, isLoading } = useGetFootprintingDairyHerdDetails(primaryDataId ?? "");

  const { mutate: mutateDairyHerdDetails } = useMutateFootprintingDairyHerdDetails(
    farmId,
    year,
    {
      matFeedPct,
      wholeMilkFeedPct,
      ageOfFirstCalvingMonths,
      timeBetweenCalvingDays,
    },
    primaryDataId
  );

  const handleMatOrWholeMilkFeedChange = (value: number | null, target: "mat" | "wholeMilk") => {
    const pct = formatPercentageInput(value);

    if (target === "mat") {
      setMatFeedPct(pct);
    } else {
      setWholeMilkFeedPct(pct);
    }

    if (pct !== null && isValidPercentageDecimal(value ?? 0)) {
      if (target === "mat") {
        setWholeMilkFeedPct(Math.round((1 - pct) * 10000) / 10000);
      } else {
        setMatFeedPct(Math.round((1 - pct) * 10000) / 10000);
      }

      formRef.current?.setFieldValue(target === "mat" ? "wholeMilkFeedPct" : "matFeedPct", formatFractionInput(1 - pct));
    } else {
      if (target === "mat") {
        setWholeMilkFeedPct(null);
      } else {
        setMatFeedPct(null);
      }

      formRef.current?.setFieldValue(target === "mat" ? "wholeMilkFeedPct" : "matFeedPct", null);
    }
  };

  useEffect(() => {
    if (dairyHerdDetails && isSuccess) {
      setMatFeedPct(dairyHerdDetails.matFeedPct ?? null);
      setWholeMilkFeedPct(dairyHerdDetails.wholeMilkFeedPct ?? null);
      setAgeOfFirstCalvingMonths(dairyHerdDetails.ageOfFirstCalvingMonths ?? null);
      setTimeBetweenCalvingDays(dairyHerdDetails.timeBetweenCalvingDays ?? null);

      formRef.current?.setFieldsValue({
        matFeedPct: formatFractionInput(dairyHerdDetails.matFeedPct ?? null),
        wholeMilkFeedPct: formatFractionInput(dairyHerdDetails.wholeMilkFeedPct ?? null),
        ageOfFirstCalvingMonths: dairyHerdDetails.ageOfFirstCalvingMonths ?? null,
        timeBetweenCalvingDays: dairyHerdDetails.timeBetweenCalvingDays ?? null,
      });

      const fieldsToValidate = (["matFeedPct", "wholeMilkFeedPct", "ageOfFirstCalvingMonths", "timeBetweenCalvingDays"] as const).some(
        (field) => dairyHerdDetails[field] !== null && dairyHerdDetails[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyHerdDetails, isSuccess]);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyHerdDetails();
      }
    },
    [isLoading, mutateDairyHerdDetails, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  const handleFieldsChange = () => {
    const timeBetweenErrors = formRef.current?.getFieldError("timeBetweenCalvingDays") || [];
    setHasTimeBetweenError(timeBetweenErrors.length > 0);
  };

  return (
    <Card border dataTestId="footprinting-dairy-offspring" className="padding-200">
      <Form ref={formRef} onFieldsChange={handleFieldsChange}>
        <Flex vertical gap={16}>
          <Typography variant="h3">{t("dairyHerdOffspringForm.question.calvesFeedTitle")}</Typography>

          <InputNumber
            className="floating-label"
            name="matFeedPct"
            disabled={readOnly}
            dataTestId="footprinting-dairy-mat-feed-pct-input"
            label={t("dairyHerdOffspringForm.question.matFeedPctInput")}
            suffix="%"
            value={formatFractionInput(matFeedPct)}
            initialValue={formatFractionInput(matFeedPct)}
            onChange={(v) => handleMatOrWholeMilkFeedChange(v, "mat")}
            min={0}
            validateFirst
            rules={[
              { required: true, message: t("dairyHerdOffspringForm.error.validPercentage") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                    return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validPercentage")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
            {t("dairyHerdOffspringForm.question.matFeedPctDescription")}
          </Typography>

          <InputNumber
            className="floating-label"
            name="wholeMilkFeedPct"
            disabled={readOnly}
            dataTestId="footprinting-dairy-whole-milk-feed-pct-input"
            label={t("dairyHerdOffspringForm.question.wholeMilkFeedPctInput")}
            suffix="%"
            value={formatFractionInput(wholeMilkFeedPct)}
            initialValue={formatFractionInput(wholeMilkFeedPct)}
            onChange={(v) => handleMatOrWholeMilkFeedChange(v, "wholeMilk")}
            min={0}
            validateFirst
            rules={[
              { required: true, message: t("dairyHerdOffspringForm.error.validPercentage") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                    return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validPercentage")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Typography variant="paragraph-s" color="grey-600" className="margin-top-50">
            {t("dairyHerdOffspringForm.question.wholeMilkFeedPctDescription")}
          </Typography>

          <InputNumber
            className="floating-label"
            name="ageOfFirstCalvingMonths"
            disabled={readOnly}
            dataTestId="footprinting-dairy-age-of-first-calving-input"
            label={t("dairyHerdOffspringForm.question.ageOfFirstCalvingInput")}
            suffix={t("dairyHerdOffspringForm.question.ageOfFirstCalvingSuffix")}
            value={ageOfFirstCalvingMonths}
            initialValue={ageOfFirstCalvingMonths}
            onChange={(v) => setAgeOfFirstCalvingMonths(v)}
            validateFirst
            rules={[
              { required: true, message: t("dairyHerdOffspringForm.error.validNumberMonths") },
              { type: "number" },
              {
                validator: (_: unknown, v: number) => {
                  if (isNullOrUndefined(v) || !isWithinBounds(v, 18, 60)) {
                    return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validNumberMonths")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
          <Form.Item className={hasTimeBetweenError ? "padding-bottom-200" : ""}>
            <InputNumber
              className="floating-label"
              name="timeBetweenCalvingDays"
              disabled={readOnly}
              dataTestId={"footprinting-farm-dairy-time-between-calving-input"}
              label={t("dairyHerdOffspringForm.question.timeBetweenCalvingInput")}
              suffix={t("dairyHerdOffspringForm.question.timeBetweenCalvingSuffix")}
              value={timeBetweenCalvingDays}
              initialValue={timeBetweenCalvingDays}
              onChange={(v) => setTimeBetweenCalvingDays(v)}
              validateFirst
              rules={[
                { required: true, message: t("dairyHerdOffspringForm.error.validNumberDays") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 300, 450)) {
                      return Promise.reject(new Error(t("dairyHerdOffspringForm.error.validNumberDays")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Card>
  );
};

HerdOffspringForm.Key = "dairy.herdOffspring";
export { HerdOffspringForm };
